import { HttpClient, HttpHeaders } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Observable, tap } from "rxjs";
import { INTERNAL_APP_CONFIG } from "../token";

interface TokenResponse {
    id_token: string;
    access_token: string;
    refresh_token: string;
    expires_in: number;
    token_type: string;
}

interface DecodedToken {
    [key: string]: unknown;
    exp: number;
    iat: number;
    sub: string;
    email?: string;
}

@Injectable({
    providedIn: "root",
})
export class CognitoAuthService {
    private readonly _config = inject(INTERNAL_APP_CONFIG);
    private readonly _http = inject(HttpClient);
    private readonly _router = inject(Router);

    private readonly _storageKeys = {
        ID_TOKEN: "id_token",
        ACCESS_TOKEN: "access_token",
        REFRESH_TOKEN: "refresh_token",
        CODE_VERIFIER: "code_verifier",
    };

    /**
     * Verifica se l'autenticazione Cognito è abilitata
     */
    get isEnabled(): boolean {
        return this._config.authentication.cognito.enabled;
    }

    /**
     * Verifica se l'utente è autenticato controllando la validità del token
     */
    isAuthenticated(): boolean {
        if (!this.isEnabled) {
            return true; // Se Cognito non è abilitato, permettere accesso
        }

        const token = this.getIdToken();
        if (!token) {
            return false;
        }

        return !this._isTokenExpired(token);
    }

    /**
     * Ottiene l'ID token dal sessionStorage
     */
    getIdToken(): string | null {
        return sessionStorage.getItem(this._storageKeys.ID_TOKEN);
    }

    /**
     * Ottiene l'access token dal sessionStorage
     */
    getAccessToken(): string | null {
        return sessionStorage.getItem(this._storageKeys.ACCESS_TOKEN);
    }

    /**
     * Ottiene i dati dell'utente decodificando l'ID token
     */
    getUserData(): DecodedToken | null {
        const token = this.getIdToken();
        if (!token) {
            return null;
        }

        try {
            return this._decodeToken(token);
        } catch {
            return null;
        }
    }

    /**
     * Avvia il flusso di login reindirizzando a Cognito Hosted UI
     */
    async login(): Promise<void> {
        if (!this.isEnabled) {
            return;
        }

        const { codeVerifier, codeChallenge } = await this._generatePKCE();
        sessionStorage.setItem(this._storageKeys.CODE_VERIFIER, codeVerifier);

        const cognitoConfig = this._config.authentication.cognito;
        const params = new URLSearchParams({
            client_id: cognitoConfig.clientId,
            response_type: "code",
            scope: "openid email phone",
            redirect_uri: cognitoConfig.redirectUri || window.location.origin + "/auth/callback",
            code_challenge: codeChallenge,
            code_challenge_method: "S256",
        });

        const loginUrl = `https://${cognitoConfig.domain}/oauth2/authorize?${params.toString()}`;
        window.location.href = loginUrl;
    }

    /**
     * Gestisce il callback da Cognito scambiando il code con i token
     */
    handleCallback(code: string): Observable<TokenResponse> {
        const codeVerifier = sessionStorage.getItem(this._storageKeys.CODE_VERIFIER);
        if (!codeVerifier) {
            throw new Error("Code verifier not found");
        }

        const cognitoConfig = this._config.authentication.cognito;
        const tokenUrl = `https://${cognitoConfig.domain}/oauth2/token`;

        const body = new URLSearchParams({
            grant_type: "authorization_code",
            client_id: cognitoConfig.clientId,
            code,
            redirect_uri: cognitoConfig.redirectUri || window.location.origin + "/auth/callback",
            code_verifier: codeVerifier,
        });

        const headers = new HttpHeaders({
            "Content-Type": "application/x-www-form-urlencoded",
        });

        return this._http.post<TokenResponse>(tokenUrl, body.toString(), { headers }).pipe(
            tap((response) => {
                this._storeTokens(response);
                sessionStorage.removeItem(this._storageKeys.CODE_VERIFIER);
            }),
        );
    }

    /**
     * Effettua il logout rimuovendo i token e reindirizzando a Cognito
     */
    logout(): void {
        this._clearTokens();

        if (!this.isEnabled) {
            this._router.navigate(["/"]);
            return;
        }

        const cognitoConfig = this._config.authentication.cognito;
        const logoutUri = cognitoConfig.logoutUri || window.location.origin;

        const params = new URLSearchParams({
            client_id: cognitoConfig.clientId,
            logout_uri: logoutUri,
        });

        const logoutUrl = `https://${cognitoConfig.domain}/logout?${params.toString()}`;
        window.location.href = logoutUrl;
    }

    /**
     * Genera code_verifier e code_challenge per PKCE
     */
    private async _generatePKCE(): Promise<{ codeVerifier: string; codeChallenge: string }> {
        const codeVerifier = this._generateRandomString(128);
        const hash = await this._sha256(codeVerifier);
        const codeChallenge = this._base64UrlEncode(hash);
        return { codeVerifier, codeChallenge };
    }

    /**
     * Genera una stringa random per PKCE
     */
    private _generateRandomString(length: number): string {
        const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
        const randomValues = new Uint8Array(length);
        crypto.getRandomValues(randomValues);
        return Array.from(randomValues)
            .map((value) => charset[value % charset.length])
            .join("");
    }

    /**
     * Calcola SHA-256 hash
     */
    private async _sha256(plain: string): Promise<ArrayBuffer> {
        const encoder = new TextEncoder();
        const data = encoder.encode(plain);
        return crypto.subtle.digest("SHA-256", data);
    }

    /**
     * Converte ArrayBuffer in base64url
     */
    private _base64UrlEncode(arrayBuffer: ArrayBuffer): string {
        const bytes = new Uint8Array(arrayBuffer);
        let binary = "";
        for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
    }

    /**
     * Decodifica un JWT token
     */
    private _decodeToken(token: string): DecodedToken {
        const parts = token.split(".");
        if (parts.length !== 3) {
            throw new Error("Invalid token format");
        }

        const payload = parts[1];
        const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
        return JSON.parse(decoded);
    }

    /**
     * Verifica se un token è scaduto
     */
    private _isTokenExpired(token: string): boolean {
        try {
            const decoded = this._decodeToken(token);
            const expirationTime = decoded.exp * 1000;
            return Date.now() >= expirationTime;
        } catch {
            return true;
        }
    }

    /**
     * Salva i token nel sessionStorage
     */
    private _storeTokens(response: TokenResponse): void {
        sessionStorage.setItem(this._storageKeys.ID_TOKEN, response.id_token);
        sessionStorage.setItem(this._storageKeys.ACCESS_TOKEN, response.access_token);
        if (response.refresh_token) {
            sessionStorage.setItem(this._storageKeys.REFRESH_TOKEN, response.refresh_token);
        }
    }

    /**
     * Rimuove tutti i token dal sessionStorage
     */
    private _clearTokens(): void {
        sessionStorage.removeItem(this._storageKeys.ID_TOKEN);
        sessionStorage.removeItem(this._storageKeys.ACCESS_TOKEN);
        sessionStorage.removeItem(this._storageKeys.REFRESH_TOKEN);
        sessionStorage.removeItem(this._storageKeys.CODE_VERIFIER);
    }
}
